import React, { useState } from "react";
import { useQuery, useQueryClient, keepPreviousData } from "@tanstack/react-query";
import { getLeadTimes, setLeadTime, removeLeadTime, bulkLeadTime, getProcurementAvgLeadTime, downloadActualLeadTimes, api } from "@/lib/api";
import { fmtNum, fmtLt } from "@/lib/format";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import { Search, Check, RotateCcw, Upload, ChevronLeft, ChevronRight, Lock, Timer, Download } from "lucide-react";
import { useAuth } from "@/lib/auth";
import { toast } from "sonner";

export default function LeadTimeManager() {
  const qc = useQueryClient();
  const { isAdmin } = useAuth();
  const [search, setSearch] = useState("");
  const [box, setBox] = useState("");
  const [overridesOnly, setOverridesOnly] = useState(false);
  const [page, setPage] = useState(1);
  const [edits, setEdits] = useState({});
  const [bulkCodes, setBulkCodes] = useState("");
  const [bulkLt, setBulkLt] = useState("3");
  const [ltLookup, setLtLookup] = useState("");
  const [ltResult, setLtResult] = useState(null);
  const [ltLoading, setLtLoading] = useState(false);
  const [ltExporting, setLtExporting] = useState(false);

  const exportActual = async () => {
    setLtExporting(true);
    toast.info("Exporting actual lead times…");
    try { await downloadActualLeadTimes(); toast.success("Actual lead times exported to Excel"); }
    catch { toast.error("Export failed — ensure procurement data is loaded"); }
    finally { setLtExporting(false); }
  };

  const doLtLookup = async () => {
    const code = ltLookup.trim();
    if (!code || Number.isNaN(Number(code))) { toast.error("Enter a valid Item Code"); return; }
    setLtLoading(true);
    try { setLtResult(await getProcurementAvgLeadTime(code)); }
    catch (e) { toast.error(e?.response?.data?.detail || "Lookup failed — ensure procurement data is loaded"); setLtResult(null); }
    finally { setLtLoading(false); }
  };

  const params = { page, page_size: 50, search: search || undefined, overrides_only: overridesOnly ? "1" : undefined };
  const { data, isFetching } = useQuery({ queryKey: ["lead-times", params], queryFn: () => getLeadTimes(params), placeholderData: keepPreviousData });
  const invalidate = () => { qc.invalidateQueries({ queryKey: ["lead-times"] }); qc.invalidateQueries({ queryKey: ["items"] }); };

  const save = async (item_no) => {
    const v = edits[item_no];
    if (v === undefined || v === "" || Number.isNaN(Number(v))) { toast.error("Enter a valid lead time"); return; }
    await setLeadTime({ item_no, manual_lead_time: Number(v) });
    toast.success(`Lead time saved for ${item_no}`);
    setEdits((e) => { const n = { ...e }; delete n[item_no]; return n; });
    invalidate();
  };
  const revert = async (item_no) => { await removeLeadTime(item_no); toast.success(`Override removed for ${item_no}`); invalidate(); };

  const applyBulk = async () => {
    if (!bulkCodes.trim() || bulkLt === "" || Number.isNaN(Number(bulkLt))) { toast.error("Enter Item Codes and a lead time"); return; }
    const res = await bulkLeadTime({ item_nos: bulkCodes, manual_lead_time: Number(bulkLt) });
    toast.success(`Applied to ${res.applied} items${res.not_found.length ? `, ${res.not_found.length} not found` : ""}`);
    setBulkCodes(""); invalidate();
  };

  const uploadFile = async (file) => {
    const fd = new FormData(); fd.append("file", file);
    try {
      const res = await api.post("/lead-times/bulk-upload", fd);
      toast.success(`Uploaded: ${res.data.applied} applied, ${res.data.skipped} skipped`);
      invalidate();
    } catch (e) { toast.error(e?.response?.data?.detail || "Upload failed"); }
  };

  const rows = data?.rows || [];
  const pages = Math.max(1, Math.ceil((data?.total || 0) / 50));

  return (
    <div className="p-6 space-y-4">
      <div>
        <div className="label-eyebrow">Configuration</div>
        <h1 className="text-2xl font-bold text-slate-900 tracking-tight">Lead Time Manager</h1>
        <p className="text-sm text-slate-500 mt-0.5">Manual overrides are keyed by Item Code and survive every daily upload.</p>
      </div>

      {!isAdmin && (
        <div className="flex items-center gap-2 text-[13px] text-amber-700 bg-amber-50 border border-amber-200 rounded-md px-3 py-2">
          <Lock className="w-4 h-4" /> Read-only account — lead times are visible but cannot be edited.
        </div>
      )}

      {isAdmin && (
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <div className="kcard p-4 space-y-3">
          <div className="label-eyebrow">Bulk Lead Time Update</div>
          <textarea value={bulkCodes} onChange={(e) => setBulkCodes(e.target.value)} rows={4} data-testid="ltm-bulk-codes"
            placeholder={"11407\n15198\n3036\n9459"} className="w-full rounded-md border border-[var(--line)] p-2.5 text-[13px] num outline-none focus:border-slate-400 resize-none slim" />
          <div className="flex items-center gap-2">
            <div className="flex items-center gap-2">
              <span className="text-[12px] text-slate-500">New Lead Time</span>
              <input value={bulkLt} onChange={(e) => setBulkLt(e.target.value)} data-testid="ltm-bulk-lt" className="w-20 h-9 rounded-md border border-[var(--line)] px-2 text-[13px] num" />
              <span className="text-[12px] text-slate-400">months</span>
            </div>
            <button onClick={applyBulk} data-testid="ltm-apply-bulk" className="ml-auto h-9 px-4 rounded-md text-white text-[13px] font-medium" style={{ background: "var(--kcsa-red)" }}>Apply to Selected Items</button>
          </div>
        </div>

        <div className="kcard p-4 flex flex-col justify-center">
          <div className="label-eyebrow mb-2">Bulk Upload (CSV / Excel)</div>
          <label className="border-2 border-dashed border-[var(--line)] rounded-lg p-5 text-center cursor-pointer hover:border-slate-400" data-testid="ltm-upload-label">
            <Upload className="w-6 h-6 mx-auto text-slate-400" />
            <div className="text-[13px] text-slate-600 mt-2">Upload a file with <b>ItemNo</b> and <b>Manual Lead Time</b> columns</div>
            <input type="file" accept=".csv,.xlsx,.xls" className="hidden" data-testid="ltm-upload-input" onChange={(e) => e.target.files[0] && uploadFile(e.target.files[0])} />
          </label>
        </div>
      </div>
      )}

      {isAdmin && (
      <div className="kcard p-4 space-y-3" data-testid="ltm-avg-lookup">
        <div className="flex items-center gap-2"><Timer className="w-4 h-4 text-slate-400" /><div className="label-eyebrow">Average Delivered Lead Time Lookup</div>
          <button onClick={exportActual} disabled={ltExporting} data-testid="ltm-export-actual-btn" className="ml-auto h-8 px-3 rounded-md text-white text-[12px] font-medium flex items-center gap-1.5 disabled:opacity-60" style={{ background: "var(--kcsa-red)" }}><Download className="w-3.5 h-3.5" /> {ltExporting ? "Exporting…" : "Export All to Excel"}</button></div>
        <p className="text-[12px] text-slate-500 -mt-1">Actual lead time from procurement delivery history (days) — handy to sanity-check manual overrides.</p>
        <form onSubmit={(e) => { e.preventDefault(); doLtLookup(); }} className="flex items-center gap-2">
          <input value={ltLookup} onChange={(e) => setLtLookup(e.target.value)} data-testid="ltm-avg-input"
            placeholder="Enter Item Code e.g. 36200" className="w-52 h-9 rounded-md border border-[var(--line)] px-3 text-[13px] num outline-none focus:border-slate-400" />
          <button type="submit" disabled={ltLoading} data-testid="ltm-avg-btn" className="h-9 px-4 rounded-md text-white text-[13px] font-medium disabled:opacity-60" style={{ background: "var(--kcsa-red)" }}>{ltLoading ? "Looking up…" : "Look up"}</button>
        </form>
        {ltResult && (ltResult.found ? (
          <div className="rounded-md border border-sky-200 bg-sky-50 px-4 py-3" data-testid="ltm-avg-result">
            <div className="flex items-baseline gap-2 flex-wrap">
              <span className="num font-bold text-slate-800">{ltResult.item_no}</span>
              <span className="text-[13px] text-slate-600">{ltResult.desc}</span>
            </div>
            <div className="mt-1 flex items-baseline gap-2 flex-wrap">
              <span className="text-2xl font-bold num text-sky-700">{ltResult.avg_lead_time_days != null ? `${ltResult.avg_lead_time_days} days` : "–"}</span>
              <span className="text-[12px] text-slate-500">avg delivered lead time · {fmtNum(ltResult.delivered_orders)} delivered orders · {ltResult.suppliers} supplier{ltResult.suppliers === 1 ? "" : "s"}</span>
            </div>
            {ltResult.configured_effective_lt_months != null && <div className="text-[11px] text-slate-400 mt-1">Configured effective lead time: {fmtLt(ltResult.configured_effective_lt_months)}</div>}
          </div>
        ) : (
          <div className="rounded-md border border-amber-200 bg-amber-50 px-4 py-3 text-[13px] text-amber-800" data-testid="ltm-avg-result">
            No delivered procurement history found for item {ltResult.item_no}.{ltResult.configured_effective_lt_months != null ? ` Configured effective lead time: ${fmtLt(ltResult.configured_effective_lt_months)}.` : " Upload the JDE procurement report to populate delivery history."}
          </div>
        ))}
      </div>
      )}

      <div className="kcard p-4 flex items-center gap-3">
        <form onSubmit={(e) => { e.preventDefault(); setSearch(box.trim()); setPage(1); }} className="relative flex-1 max-w-md">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
          <input value={box} onChange={(e) => setBox(e.target.value)} data-testid="ltm-search" placeholder="Search Item Code / description…" className="w-full h-9 pl-9 pr-3 rounded-md border border-[var(--line)] text-[13px] outline-none focus:border-slate-400" />
        </form>
        <label className="flex items-center gap-2 text-[13px] text-slate-600"><Switch checked={overridesOnly} onCheckedChange={(v) => { setOverridesOnly(v); setPage(1); }} data-testid="ltm-overrides-only" /> Overrides only</label>
      </div>

      <div className="kcard p-0 overflow-hidden">
        <div className="flex items-center justify-between px-4 py-2 border-b border-[var(--line)]">
          <span className="text-[13px] text-slate-500"><b className="num text-slate-800">{fmtNum(data?.total || 0)}</b> items{isFetching ? " · loading…" : ""}</span>
          <Pager page={page} pages={pages} setPage={setPage} />
        </div>
        <div className="overflow-x-auto slim">
          <table className="w-full text-[13px]">
            <thead className="bg-[var(--surface)]">
              <tr>{["Item Code", "Description", "Class", "Preferred Supplier", "Source LT", "Manual LT", "Effective LT", "Override", "Last Modified", ""].map((h) => <th key={h} className="thead-cell px-3 py-2.5 text-left whitespace-nowrap">{h}</th>)}</tr>
            </thead>
            <tbody>
              {rows.map((r) => {
                const editing = edits[r.item_no] !== undefined;
                return (
                  <tr key={r.item_no} className="border-t border-[var(--line)] hover:bg-slate-50">
                    <td className="px-3 py-2 num font-semibold text-[var(--kcsa-red-dark)]">{r.item_no}</td>
                    <td className="px-3 py-2 min-w-[180px]">{r.desc}</td>
                    <td className="px-3 py-2 text-slate-600">{r.klass}</td>
                    <td className="px-3 py-2 text-slate-600">{r.pref_supp}</td>
                    <td className="px-3 py-2 num text-slate-600">{fmtLt(r.source_lt)}</td>
                    <td className="px-3 py-2">
                      <input value={editing ? edits[r.item_no] : (r.manual_lt ?? "")} onChange={(e) => setEdits((s) => ({ ...s, [r.item_no]: e.target.value }))}
                        disabled={!isAdmin} placeholder="–" data-testid={`ltm-input-${r.item_no}`} className="w-20 h-8 rounded border border-[var(--line)] px-2 text-[13px] num focus:border-slate-400 outline-none disabled:bg-slate-50 disabled:text-slate-400" />
                    </td>
                    <td className="px-3 py-2 num font-medium">{fmtLt(r.effective_lt)}</td>
                    <td className="px-3 py-2">{r.lt_source === "manual" ? <Badge variant="outline" className="bg-blue-50 text-blue-700 border-blue-200 rounded-full text-[11px]">Manual</Badge> : <span className="text-[11px] text-slate-400 capitalize">{r.lt_source}</span>}</td>
                    <td className="px-3 py-2 text-[12px] text-slate-500">{r.last_modified ? new Date(r.last_modified).toLocaleDateString() : "–"}</td>
                    <td className="px-3 py-2">
                      {isAdmin && (
                      <div className="flex items-center gap-1.5">
                        <button onClick={() => save(r.item_no)} disabled={!editing} data-testid={`ltm-save-${r.item_no}`} className="h-7 px-2 rounded bg-slate-900 text-white text-[11px] font-medium disabled:opacity-30 flex items-center gap-1"><Check className="w-3 h-3" />Save</button>
                        {r.lt_source === "manual" && <button onClick={() => revert(r.item_no)} data-testid={`ltm-revert-${r.item_no}`} className="h-7 px-2 rounded border border-[var(--line)] text-slate-600 text-[11px] flex items-center gap-1"><RotateCcw className="w-3 h-3" />Revert</button>}
                      </div>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

function Pager({ page, pages, setPage }) {
  return (
    <div className="flex items-center gap-2 text-[12px] text-slate-600">
      <button disabled={page <= 1} onClick={() => setPage((p) => p - 1)} className="h-7 w-7 grid place-items-center rounded border border-[var(--line)] disabled:opacity-40"><ChevronLeft className="w-4 h-4" /></button>
      <span className="num">{page} / {pages}</span>
      <button disabled={page >= pages} onClick={() => setPage((p) => p + 1)} className="h-7 w-7 grid place-items-center rounded border border-[var(--line)] disabled:opacity-40"><ChevronRight className="w-4 h-4" /></button>
    </div>
  );
}
